import React, { useRef } from 'react';
import { Box, Container, Typography, Grid, Divider } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import Layout from '../components/Layout';
import CountUp from '../components/CountUp';
import './AboutUs.css';
import aboutHeroBg from '../assets/hotel/2.jpg';

// Milestone images from real projects
import milestoneImg2021 from '../assets/CAD_Pictures/real_baustelle/1.jpg';
import milestoneImg2022 from '../assets/efh_sirnach/1.jpg';
import milestoneImg2023 from '../assets/hotel/1.jpg';
import milestoneImg2024 from '../assets/füllinsdorf/1.jpg';
import milestoneImg2025 from '../assets/allschwil/1.jpg';

// Milestone Item Component
const MilestoneItem = ({ milestone, index, isEven }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: '-40% 0px -40% 0px' });

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        mb: { xs: 6, md: 8 },
        display: 'flex',
        flexDirection: { xs: isEven ? 'row' : 'row-reverse', md: isEven ? 'row' : 'row-reverse' },
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 0, md: 0 }
      }}>
      {/* Content Side */}
      <Box
        sx={{
          flex: { xs: 1, md: 1 },
          pr: { xs: isEven ? 2 : 0, sm: isEven ? 3 : 0, md: isEven ? 6 : 0 },
          pl: { xs: isEven ? 0 : 2, sm: isEven ? 0 : 3, md: isEven ? 0 : 6 },
          textAlign: { xs: isEven ? 'right' : 'left', md: isEven ? 'right' : 'left' }
        }}>
        <motion.div
          initial={{ opacity: 0, x: isEven ? -30 : 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: index * 0.1 }}>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              color: '#1a1a1a',
              fontSize: { xs: '1.35rem', sm: '1.5rem', md: '2rem' },
              mb: { xs: 0.5, md: 1 }
            }}>
            {milestone.year}
          </Typography>
          <Typography
            variant='h5'
            sx={{
              fontWeight: 600,
              color: '#1a1a1a',
              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.5rem' },
              mb: { xs: 1, md: 2 }
            }}>
            {milestone.title}
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: '#6b6b6b',
              lineHeight: 1.7,
              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' }
            }}>
            {milestone.description}
          </Typography>
        </motion.div>
      </Box>

      {/* Center Dot - All Devices */}
      <Box
        sx={{
          position: { xs: 'relative', md: 'absolute' },
          left: { md: '50%' },
          transform: { md: 'translateX(-50%)' },
          width: { xs: '14px', sm: '16px', md: '20px' },
          height: { xs: '14px', sm: '16px', md: '20px' },
          borderRadius: '50%',
          backgroundColor: { xs: '#1a1a1a', md: isInView ? '#1a1a1a' : '#d0d0d0' },
          border: { xs: '3px solid #fff', md: '4px solid #fff' },
          boxShadow: { xs: '0 0 0 2px #1a1a1a', md: isInView ? '0 0 0 2px #1a1a1a, 0 0 20px rgba(26, 26, 26, 0.4)' : '0 0 0 2px #e0e0e0' },
          zIndex: 2,
          flexShrink: 0,
          mt: { xs: '22px', sm: '24px', md: 0 },
          mx: { xs: 2, sm: 3, md: 0 },
          transition: 'all 0.3s ease'
        }}
      />

      {/* Image Side */}
      <Box
        sx={{
          flex: { xs: 1, md: 1 },
          pr: { xs: isEven ? 0 : 2, sm: isEven ? 0 : 3, md: isEven ? 0 : 6 },
          pl: { xs: isEven ? 2 : 0, sm: isEven ? 3 : 0, md: isEven ? 6 : 0 }
        }}>
        <motion.div
          initial={{ opacity: 0, x: isEven ? 30 : -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: index * 0.1 + 0.2 }}>
          <Box
            component='img'
            src={milestone.image}
            alt={milestone.title}
            sx={{
              width: '100%',
              height: { xs: '180px', sm: '220px', md: '280px' },
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
              }
            }}
          />
        </motion.div>
      </Box>
    </Box>
  );
};

export const AboutUs = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const stats = [
    { number: 4, suffix: '+', label: 'Jahre Erfahrung', delay: 0 },
    { number: 18, suffix: '+', label: 'Projekte in 2025', delay: 0.2 },
    { number: 10, suffix: '+', label: 'Jahre Branchenerfahrung', delay: 0.4 },
    { number: 2, suffix: '', label: 'Partner', delay: 0.6 }
  ];

  const milestones = [
    {
      year: '2021',
      title: 'Gründung',
      description:
        'Gründung des Büros als Planungsunternehmen mit Fokus auf effiziente und präzise Projektbearbeitung.',
      image: milestoneImg2021
    },
    {
      year: '2022',
      title: 'Erweiterung um Architektur',
      description:
        'Mit dem Eintritt von Muhamed Jonuzi wurde das Leistungsspektrum um Architektur erweitert und die gestalterische Kompetenz des Büros gezielt ausgebaut.',
      image: milestoneImg2022
    },
    {
      year: '2023',
      title: 'Partnerschaften',
      description:
        'Zusammenarbeit mit verschiedenen Architekturbüros im Rahmen von Partnerschaften an unterschiedlichen Projekten.',
      image: milestoneImg2023
    },
    {
      year: '2024',
      title: 'Erstes eigenständiges Projekt',
      description:
        'Realisierung des ersten eigenständigen Projekts – ein wichtiger Meilenstein in der Entwicklung des Büros.',
      image: milestoneImg2024
    },
    {
      year: '2025',
      title: 'Starkes Wachstum',
      description:
        'Starkes Wachstum und Beteiligung an über 18 Projekten in unterschiedlichen Planungs- und Realisierungsphasen.',
      image: milestoneImg2025
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <Box className='hero-section-about' style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${aboutHeroBg})` }}>
        <Box className='hero-overlay-about'>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography
              variant='h1'
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' },
                fontWeight: 700,
                letterSpacing: { xs: '2px', md: '8px' },
                mb: 2,
                textShadow: '2px 2px 12px rgba(0, 0, 0, 0.7)'
              }}>
              ÜBER UNS
            </Typography>
            <Typography
              variant='h5'
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: 300,
                letterSpacing: { xs: '1px', md: '2px' },
                textShadow: '1px 1px 6px rgba(0, 0, 0, 0.7)'
              }}>
              Architektonische Exzellenz seit 2021
            </Typography>
          </motion.div>
        </Box>
      </Box>

      {/* Our Story Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#fff' }}>
        <Container maxWidth='lg'>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant='overline'
                sx={{
                  color: '#999',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  letterSpacing: '3px',
                  fontWeight: 500,
                  mb: 2.5,
                  display: 'block',
                  textTransform: 'uppercase'
                }}>
                ÜBER UNS
              </Typography>
              <Typography
                variant='h2'
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem', lg: '3rem' },
                  mb: 4,
                  lineHeight: 1.2,
                  color: '#1a1a1a',
                  maxWidth: '900px',
                  mx: 'auto'
                }}>
                Präzision, Gestaltung und Wirtschaftlichkeit
              </Typography>
            </Box>

            <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
              <Typography
                variant='body1'
                sx={{
                  lineHeight: 1.9,
                  color: '#6b6b6b',
                  fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                  mb: 3,
                  fontWeight: 400,
                  textAlign: 'center'
                }}>
                Wir verstehen Architektur als präzises Zusammenspiel von Gestaltung, Funktion und Wirtschaftlichkeit. Unser Anspruch ist es,
                zeitlose Lösungen von hoher architektonischer Qualität zu schaffen – mit Klarheit im Entwurf, Sorgfalt in der Planung und
                Konsequenz in der Umsetzung.
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  lineHeight: 1.9,
                  color: '#6b6b6b',
                  fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                  mb: 3,
                  fontWeight: 400,
                  textAlign: 'center'
                }}>
                Als partnerschaftlich geführtes Büro vereinen wir gestalterische Expertise mit unternehmerischem Denken. So begleiten wir Projekte
                ganzheitlich und mit einem hohen Anspruch an Präzision, Effizienz und Wertigkeit. Unser Team wird ergänzt durch weitere
                qualifizierte Mitarbeitende aus den Bereichen Architektur und Zeichnung.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Unser Team Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth='lg'>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Divider
              sx={{
                width: '80px',
                height: '3px',
                backgroundColor: '#1a1a1a',
                margin: '0 auto 3rem',
                border: 'none'
              }}
            />
            <Typography
              variant='h2'
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem' },
                mb: 2,
                textAlign: 'center',
                color: '#1a1a1a'
              }}>
              Unser Team
            </Typography>
            <Typography
              variant='body1'
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.8,
                color: '#6b6b6b',
                textAlign: 'center',
                maxWidth: '700px',
                mx: 'auto',
                mb: { xs: 6, md: 8 }
              }}>
              Die Menschen hinter Plannic – mit Leidenschaft, Fachkompetenz und dem Anspruch, jedes Projekt auf höchstem Niveau zu realisieren.
            </Typography>
          </motion.div>

          {/* Liridon Jahja - Photo Left, Text Right */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 4, md: 6, lg: 8 },
              mb: { xs: 8, md: 10 }
            }}>
            <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: '320px', md: '380px' } }}>
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: '360px', sm: '420px', md: '480px' },
                    backgroundColor: '#e8e8e8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                  {/* Placeholder – replace src with actual photo */}
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#d5d5d5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <Typography sx={{ color: '#999', fontSize: '0.9rem', letterSpacing: '0.05em' }}>FOTO</Typography>
                  </Box>
                </Box>
              </motion.div>
            </Box>

            <Box sx={{ flex: 1 }}>
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 }}>
                <Typography
                  variant='overline'
                  sx={{
                    color: '#999',
                    fontSize: '0.75rem',
                    letterSpacing: '3px',
                    fontWeight: 500,
                    mb: 1,
                    display: 'block'
                  }}>
                  BETRIEBSÖKONOM / BAUMANAGEMENT / PARTNER
                </Typography>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                    mb: 3,
                    color: '#1a1a1a',
                    lineHeight: 1.2
                  }}>
                  Liridon Jaja
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    lineHeight: 1.9,
                    color: '#6b6b6b',
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                    mb: 2,
                    fontWeight: 400
                  }}>
                  Liridon Jaja verfügt über mehr als 10 Jahre Erfahrung in der Bau- und Projektentwicklung. Sein Schwerpunkt liegt in der
                  strategischen Steuerung, Kostenkontrolle und wirtschaftlichen Optimierung von Bauvorhaben.
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    lineHeight: 1.9,
                    color: '#6b6b6b',
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                    fontWeight: 400
                  }}>
                  Als CFO eines Unternehmens in der Medizintechnik verbindet er fundierte betriebswirtschaftliche Kompetenz mit
                  unternehmerischer Weitsicht.
                </Typography>
              </motion.div>
            </Box>
          </Box>

          {/* Muhamed Jonuzi - Text Left, Photo Right */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row-reverse' },
              alignItems: 'center',
              gap: { xs: 4, md: 6, lg: 8 }
            }}>
            <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: '320px', md: '380px' } }}>
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: '360px', sm: '420px', md: '480px' },
                    backgroundColor: '#e8e8e8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                  {/* Placeholder – replace src with actual photo */}
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#d5d5d5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <Typography sx={{ color: '#999', fontSize: '0.9rem', letterSpacing: '0.05em' }}>FOTO</Typography>
                  </Box>
                </Box>
              </motion.div>
            </Box>

            <Box sx={{ flex: 1 }}>
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 }}>
                <Typography
                  variant='overline'
                  sx={{
                    color: '#999',
                    fontSize: '0.75rem',
                    letterSpacing: '3px',
                    fontWeight: 500,
                    mb: 1,
                    display: 'block'
                  }}>
                  ARCHITEKT / PARTNER
                </Typography>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                    mb: 3,
                    color: '#1a1a1a',
                    lineHeight: 1.2
                  }}>
                  Muhamed Jonuzi
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    lineHeight: 1.9,
                    color: '#6b6b6b',
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                    mb: 2,
                    fontWeight: 400
                  }}>
                  Mit über 12 Jahren Erfahrung in der Architektur entwickelt Muhamed Jonuzi präzise und eigenständige Entwürfe. Sein Fokus
                  liegt auf einer klaren architektonischen Haltung, ausgewogenen Proportionen und der konsequenten Umsetzung anspruchsvoller
                  Projekte über alle Planungsphasen hinweg.
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    lineHeight: 1.9,
                    color: '#6b6b6b',
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                    fontWeight: 400
                  }}>
                  Ausbildung: BArch, UKIM.
                </Typography>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth='lg'>
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent='center'>
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: stat.delay, duration: 0.5 }}>
                  <Box sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 0 } }}>
                    <Typography
                      variant='h3'
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                        mb: 1,
                        color: '#1a1a1a'
                      }}>
                      <CountUp from={0} to={stat.number} duration={2.5} delay={stat.delay} />
                      {stat.suffix}
                    </Typography>
                    <Typography
                      variant='body1'
                      sx={{
                        fontSize: { xs: '0.85rem', md: '1rem' },
                        color: '#6b6b6b',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Values Section */}
      {/* <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#fff' }}>
        <Container maxWidth='lg'>
          <motion.div variants={containerVariants} initial='hidden' whileInView='visible' viewport={{ once: true }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant='overline'
                sx={{
                  color: '#999',
                  fontSize: '0.75rem',
                  letterSpacing: '3px',
                  fontWeight: 500,
                  mb: 2,
                  display: 'block',
                  textAlign: 'center'
                }}>
                WHAT DRIVES US
              </Typography>
              <Typography
                variant='h2'
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem' },
                  mb: 8,
                  textAlign: 'center',
                  color: '#1a1a1a'
                }}>
                Our Core Values
              </Typography>
            </motion.div>

            <Grid container spacing={{ xs: 4, md: 4 }} justifyContent='center'>
              {values.map((value, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div variants={itemVariants}>
                    <Box sx={{ textAlign: 'center', px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 0 } }}>
                      <Typography
                        variant='h5'
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: '#1a1a1a',
                          fontSize: { xs: '1.25rem', md: '1.5rem' }
                        }}>
                        {value.title}
                      </Typography>
                      <Typography
                        variant='body1'
                        sx={{
                          color: '#6b6b6b',
                          lineHeight: 1.8,
                          fontSize: { xs: '0.95rem', md: '1rem' }
                        }}>
                        {value.description}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box> */}

      {/* Milestones Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#fff' }}>
        <Container maxWidth='lg'>
          <motion.div variants={containerVariants} initial='hidden' whileInView='visible' viewport={{ once: true }}>
            <motion.div variants={itemVariants}>
              <Divider
                sx={{
                  width: '80px',
                  height: '3px',
                  backgroundColor: '#1a1a1a',
                  margin: '0 auto 3rem',
                  border: 'none'
                }}
              />
              <Typography
                variant='h2'
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem' },
                  mb: 8,
                  textAlign: 'center',
                  color: '#1a1a1a'
                }}>
                Unsere Entwicklung
              </Typography>
            </motion.div>

            {/* Timeline Container */}
            <Box sx={{ position: 'relative', mt: 6 }}>
              {/* Center Line - All Devices */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  backgroundColor: '#e0e0e0',
                  transform: 'translateX(-50%)'
                }}
              />

              {milestones.map((milestone, index) => {
                const isEven = index % 2 === 0;
                return <MilestoneItem key={index} milestone={milestone} index={index} isEven={isEven} />;
              })}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Closing Statement */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#1a1a1a', color: '#fff' }}>
        <Container maxWidth='md'>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Typography
              variant='h4'
              align='center'
              sx={{
                mb: 3,
                fontWeight: 300,
                letterSpacing: '2px',
                lineHeight: 1.6,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}>
              Ausblick
            </Typography>
            <Typography
              variant='body1'
              align='center'
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                opacity: 0.9,
                lineHeight: 1.8,
                color: '#e0e0e0'
              }}>
              Unser Ziel ist ein bewusstes, nachhaltiges Wachstum. Durch eine gezielte und kontrollierte Entwicklung stellen wir sicher, dass
              Qualität, Präzision und das Vertrauen unserer Partner und Bauherrschaften jederzeit im Mittelpunkt stehen.
            </Typography>
          </motion.div>
        </Container>
      </Box>
    </Layout>
  );
};
